import type { GenericCardView } from '../../kanban/components/genericCards';
import { toDurationMs } from '../trackingUtils';
import useMtText from './useMtText';

interface IDrawerStepRowProps {
    card: GenericCardView;
    name: string;
    estimateMs?: number;
}

const DrawerStepRow = ({ card, name, estimateMs }: IDrawerStepRowProps) => {
    const { t, msg, formatDuration, formatDateTime } = useMtText();
    const repairMs = toDurationMs(card.estimatedRepairTime);
    const showAction = card.canChangeStatus && !card.statusDisabled;

    const period =
        card.status === 'todo'
            ? msg('intl-msg:occurrence.card.notStarted')
            : `${formatDateTime(card.startDate)} → ${
                  card.status === 'current' ? msg('intl-msg:occurrence.step.inProgress') : formatDateTime(card.endDate)
              }`;

    return (
        <div
            className={`mt-tl__row mt-tl__row--${card.status}${card.late ? ' mt-tl__row--late' : ''}`}
            onMouseEnter={() => card.setStatusHovered(true)}
            onMouseLeave={() => card.setStatusHovered(false)}
        >
            <button
                type='button'
                className='mt-tl__open'
                aria-label={name}
                data-clickable={card.status !== 'todo'}
                onClick={card.openForm}
            />
            <i className='mt-tl__dot' />
            <div className='mt-tl__main'>
                <div className='mt-tl__name'>
                    <b>{name}</b>
                    {card.late && (
                        <span
                            className='rioglyph rioglyph-warning-sign mt-tl__warn'
                            title={msg('intl-msg:occurrence.card.timeExceeded')}
                        />
                    )}
                    {card.passageCount > 1 && (
                        <button
                            type='button'
                            className='mt-tl__link'
                            title={msg('intl-msg:kanban.genericCards.viewPassageHistory')}
                            onClick={card.openHistory}
                        >
                            <span className='rioglyph rioglyph-refresh' />
                            {msg('intl-msg:kanban.genericCards.passage', { count: card.passageCount })}
                        </button>
                    )}
                    {showAction && (
                        <button
                            type='button'
                            className='mt-tl__action'
                            aria-busy={card.statusLoading}
                            onFocus={() => card.setStatusHovered(true)}
                            onBlur={() => card.setStatusHovered(false)}
                            onClick={card.requestStatusChange}
                        >
                            {card.statusText}
                        </button>
                    )}
                </div>
                <small className='mt-num'>
                    {period}
                    {repairMs !== undefined &&
                        ` · ${msg('intl-msg:kanban.genericCards.estimatedRepairTime')}: ${formatDuration(repairMs)}`}
                </small>
            </div>
            <div className='mt-tl__time'>
                <b className='mt-num'>{card.status === 'todo' ? '–' : formatDuration(card.elapsedMs)}</b>
                {estimateMs !== undefined && (
                    <small className='mt-num'>{t('drawer.estimate', { time: formatDuration(estimateMs) })}</small>
                )}
            </div>
        </div>
    );
};

export default DrawerStepRow;
